import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './OuraConnect.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OuraConnect = () => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const linkOuraAccount = useCallback(async (ouraUserId) => {
    if (!user?.id) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/oura/oauth/link?oura_user_id=${ouraUserId}&internal_user_id=${user.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        console.log('Oura account linked to internal user');
      } else {
        console.warn('Failed to link Oura account');
      }
    } catch (err) {
      console.error('Error linking Oura account:', err);
    }
  }, [user]);

  const checkConnectionStatus = useCallback(async (uid, skipSync = false) => {
    try {
      const response = await fetch(`${API_BASE}/api/oura/oauth/status?user_id=${uid}`);
      const data = await response.json();

      if (data.connected) {
        setIsConnected(true);
        setUserId(uid);
        if (!skipSync) {
          syncData(uid);
        }
      } else {
        setIsConnected(false);
        localStorage.removeItem('oura_user_id');
      }
    } catch (err) {
      console.error('Failed to check Oura status:', err);
      setIsConnected(false);
    }
  }, []);

  // Check for OAuth callback and stored user ID on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const returnedUserId = params.get('user_id');
    const errorParam = params.get('error');

    if (success === 'true' && returnedUserId) {
      setIsConnected(true);
      setUserId(returnedUserId);
      localStorage.setItem('oura_user_id', returnedUserId);
      linkOuraAccount(returnedUserId);
      window.history.replaceState({}, document.title, window.location.pathname);

      setSyncStatus({ type: 'success', message: 'Successfully connected to Oura Ring!' });
      setTimeout(() => setSyncStatus(null), 5000);

      // Auto-sync readiness data on first connect
      syncData(returnedUserId);
    } else if (errorParam) {
      setError(`Oura connection failed: ${errorParam}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const storedUserId = localStorage.getItem('oura_user_id');
    if (storedUserId) {
      checkConnectionStatus(storedUserId);
      linkOuraAccount(storedUserId);
    }
  }, [linkOuraAccount, checkConnectionStatus]);

  const syncData = async (uid) => {
    try {
      setSyncStatus({ type: 'info', message: 'Syncing readiness data...' });
      const response = await fetch(
        `${API_BASE}/api/oura/oauth/sync?user_id=${uid}&days=7`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        const total = (data.synced?.readiness || 0) + (data.synced?.sleep || 0);
        setSyncStatus({
          type: 'success',
          message: `Synced ${total} records from Oura`
        });
        setTimeout(() => setSyncStatus(null), 5000);
      } else {
        setSyncStatus({ type: 'error', message: 'Failed to sync Oura data' });
      }
    } catch (err) {
      console.error('Oura sync error:', err);
      setSyncStatus({ type: 'error', message: 'Failed to sync Oura data' });
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/oura/oauth/authorize`);
      const data = await response.json();

      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      await fetch(`${API_BASE}/api/oura/oauth/disconnect?user_id=${userId}`, {
        method: 'DELETE'
      });

      setIsConnected(false);
      setUserId(null);
      localStorage.removeItem('oura_user_id');

      setSyncStatus({ type: 'info', message: 'Disconnected from Oura' });
      setTimeout(() => setSyncStatus(null), 3000);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect from Oura');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="oura-connect">
      {!isConnected ? (
        <div className="oura-connect-section">
          <button
            className="oura-connect-btn"
            onClick={handleConnect}
            disabled={isLoading}
          >
            <svg className="oura-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9"/>
              <circle cx="12" cy="12" r="5"/>
            </svg>
            {isLoading ? 'Connecting...' : 'Connect to Oura Ring'}
          </button>
          <p className="oura-note">
            Sync readiness, sleep & activity data from Oura
          </p>
        </div>
      ) : (
        <div className="oura-connected">
          <div className="oura-status-row">
            <div className="oura-status">
              <svg className="oura-icon connected" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="9"/>
                <circle cx="12" cy="12" r="5"/>
              </svg>
              <span className="oura-connected-text">Connected to Oura Ring</span>
            </div>
            <div className="oura-actions">
              <button
                className="oura-sync-btn"
                onClick={() => syncData(userId)}
                disabled={isLoading || syncStatus?.type === 'info'}
              >
                Sync
              </button>
              <button
                className="oura-disconnect-btn"
                onClick={handleDisconnect}
                disabled={isLoading}
              >
                Disconnect
              </button>
            </div>
          </div>

          {syncStatus && (
            <div className={`oura-sync-message ${syncStatus.type}`}>
              {syncStatus.type === 'success' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              )}
              {syncStatus.type === 'info' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
              )}
              {syncStatus.type === 'error' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              )}
              <span>{syncStatus.message}</span>
            </div>
          )}

          {!syncStatus && (
            <p className="oura-sync-note">
              Readiness & sleep data syncs from Oura
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="oura-error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default OuraConnect;
